/**
 * bungie OAuth (PKCE, public client).
 *
 * https://github.com/Bungie-net/api/wiki/OAuth-Documentation
 *
 * flow:
 *   1. login() — generate verifier+state, persist, redirect to bungie
 *   2. on app boot, handleCallback() detects ?code=... in window.location.search,
 *      swaps for tokens, clears query string
 *   3. getAccessToken() returns a live token, refreshing if needed
 *
 * tokens persist in idb-keyval. no localStorage (xss surface).
 */

import { del, get, set } from 'idb-keyval';
import { dlog } from '$domain/debug';

const AUTH = 'https://www.bungie.net/en/oauth/authorize';
const TOKEN = 'https://www.bungie.net/Platform/App/OAuth/token/';

const CLIENT_ID = import.meta.env.VITE_BUNGIE_CLIENT_ID;
const API_KEY = import.meta.env.VITE_BUNGIE_API_KEY;

interface PendingAuth {
  verifier: string;
  state: string;
  returnTo: string;
}

interface TokenSet {
  accessToken: string;
  /** undefined when Bungie issues an access-only token (Public/PKCE client). */
  refreshToken?: string;
  /** absolute epoch ms when access_token expires */
  expiresAt: number;
  /** absolute epoch ms when refresh_token expires; missing if no refresh issued */
  refreshExpiresAt?: number;
  membershipId: string;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token?: string;
  refresh_expires_in?: number;
  membership_id: string;
}

const KEY_TOKENS = 'auth:tokens';
const KEY_PENDING = 'auth:pending';

let tokensCache: TokenSet | null | undefined;
let refreshInFlight: Promise<TokenSet> | null = null;

// Bumped on every token write or clear. UIs can subscribe to react.
// Uses BroadcastChannel so notifications cross chunk boundaries — auth.ts may
// be instantiated multiple times (main + lazy chunks) and writes in one
// instance must reach listeners in another.
const _listeners = new Set<() => void>();
const _channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('cryptarch-auth');
if (_channel) {
  _channel.addEventListener('message', () => {
    for (const fn of _listeners) {
      try {
        fn();
      } catch (e) {
        console.warn('[auth] listener', e);
      }
    }
  });
}
function notifyAuthChanged(): void {
  // Fire to listeners in THIS chunk synchronously...
  for (const fn of _listeners) {
    try {
      fn();
    } catch (e) {
      console.warn('[auth] listener', e);
    }
  }
  // ...and to OTHER chunks via broadcast (their handler runs the same loop).
  _channel?.postMessage(1);
}
export function subscribeAuth(fn: () => void): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function randomB64Url(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return b64url(buf);
}

function b64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

async function sha256B64Url(s: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return b64url(digest);
}

/** redirect URL Bungie should send the browser back to. must match registered URL. */
function redirectUri(): string {
  return `${window.location.origin}/`;
}

/** kick off the OAuth dance. caller never returns. */
export async function login(): Promise<never> {
  if (!CLIENT_ID) throw new Error('VITE_BUNGIE_CLIENT_ID is not set');

  const verifier = randomB64Url(32);
  const state = randomB64Url(16);
  const pending: PendingAuth = {
    verifier,
    state,
    returnTo: window.location.hash || '#/inventory',
  };
  await set(KEY_PENDING, pending);

  const challenge = await sha256B64Url(verifier);
  const url = new URL(AUTH);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('redirect_uri', redirectUri());

  window.location.replace(url.toString());
  // unreachable
  return new Promise<never>(() => undefined);
}

/** detect & process ?code=... on app boot. returns true if a callback was handled. */
export async function handleCallback(): Promise<boolean> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const oauthError = url.searchParams.get('error');
  const oauthErrorDesc = url.searchParams.get('error_description');

  // Bungie redirected back with an explicit error (user denied, app misconfig,
  // etc). Strip params, clean up pending state, surface a friendly message.
  if (oauthError) {
    {
      const clean = new URL(window.location.href);
      clean.search = '';
      window.history.replaceState(null, '', clean.toString());
    }
    await del(KEY_PENDING);
    const friendly =
      oauthError === 'access_denied'
        ? 'You denied the bungie.net authorization.'
        : `OAuth failed: ${oauthError}${oauthErrorDesc ? ` — ${oauthErrorDesc}` : ''}`;
    throw new Error(friendly);
  }

  if (!code || !state) return false;

  // Strip code/state from URL immediately so a SW-induced reboot or page reload
  // can't re-enter this function with the same one-shot grant.
  {
    const clean = new URL(window.location.href);
    clean.search = '';
    window.history.replaceState(null, '', clean.toString());
  }

  const pending = await get<PendingAuth>(KEY_PENDING);
  await del(KEY_PENDING);
  if (!pending || pending.state !== state) {
    // Could be a double-fire; if we already have tokens, treat as benign.
    const existing = await get<TokenSet>(KEY_TOKENS);
    if (existing) return true;
    throw new Error('oauth: state mismatch (possible CSRF)');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    client_id: CLIENT_ID,
    code_verifier: pending.verifier,
    redirect_uri: redirectUri(),
  });
  const res = await fetch(TOKEN, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      'x-api-key': API_KEY,
    },
    body,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`oauth token: ${res.status} ${detail}`);
  }
  const json = (await res.json()) as TokenResponse;
  const tokens = freshTokens(json);
  await set(KEY_TOKENS, tokens);
  tokensCache = tokens;

  // Verify the write actually persisted (catch idb partitioning / quota issues early).
  const verify = await get<TokenSet>(KEY_TOKENS);
  dlog('auth', 'signed in', {
    membership: tokens.membershipId,
    idbVerify: verify ? 'ok' : 'MISSING',
  });
  notifyAuthChanged();

  // restore the user's intended hash route.
  const clean = new URL(window.location.href);
  clean.hash = pending.returnTo;
  window.history.replaceState(null, '', clean.toString());
  // replaceState does NOT fire hashchange — wake the router manually.
  window.dispatchEvent(new HashChangeEvent('hashchange'));
  return true;
}

function freshTokens(r: TokenResponse): TokenSet {
  const now = Date.now();
  return {
    accessToken: r.access_token,
    refreshToken: r.refresh_token,
    expiresAt: now + (r.expires_in - 30) * 1000,
    refreshExpiresAt:
      typeof r.refresh_expires_in === 'number'
        ? now + (r.refresh_expires_in - 60) * 1000
        : undefined,
    membershipId: r.membership_id,
  };
}

async function loadTokens(): Promise<TokenSet | null> {
  if (tokensCache !== undefined) return tokensCache;
  const t = (await get<TokenSet>(KEY_TOKENS)) ?? null;
  tokensCache = t;
  return t;
}

async function refresh(t: TokenSet): Promise<TokenSet> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    if (!t.refreshToken) throw new Error('oauth refresh: no refresh token issued');
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: t.refreshToken,
      client_id: CLIENT_ID,
    });
    const res = await fetch(TOKEN, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        'x-api-key': API_KEY,
      },
      body,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      await del(KEY_TOKENS);
      tokensCache = null;
      throw new Error(`oauth refresh: ${res.status} ${detail}`);
    }
    const json = (await res.json()) as TokenResponse;
    const next = freshTokens(json);
    await set(KEY_TOKENS, next);
    tokensCache = next;
    return next;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  let t = await loadTokens();
  if (!t) return null;
  const now = Date.now();
  const horizon = t.refreshExpiresAt ?? t.expiresAt;
  if (now >= horizon) {
    await del(KEY_TOKENS);
    tokensCache = null;
    notifyAuthChanged();
    return null;
  }
  if (now >= t.expiresAt && t.refreshToken) t = await refresh(t);
  return t.accessToken;
}

export async function getMembershipId(): Promise<string | null> {
  const t = await loadTokens();
  return t?.membershipId ?? null;
}

export async function isAuthed(): Promise<boolean> {
  // Bypass in-memory cache: this module may be instantiated in multiple chunks
  // (eager main + lazy Inventory), and writes in one don't reach the other.
  // idb is the cross-chunk source of truth.
  const t = (await get<TokenSet>(KEY_TOKENS)) ?? null;
  tokensCache = t;
  if (!t) return false;
  // Session valid while access token (or refresh, if issued) hasn't expired.
  const horizon = t.refreshExpiresAt ?? t.expiresAt;
  return Date.now() < horizon;
}

export async function logout(): Promise<void> {
  await del(KEY_TOKENS);
  tokensCache = null;
  notifyAuthChanged();
}
